<template>
  <div class="report-layout-moderno" style="font-family: Arial, Helvetica, sans-serif;">
    <div class="bg-white shadow-sm rounded-xl overflow-hidden mb-8 border border-slate-200 print:shadow-none print:rounded-none">
      <!-- BARRA SUPERIOR (OCULTA NA IMPRESSÃO) -->
      <div class="p-4 border-b flex justify-between items-center print:hidden" style="background-color: #333333; border-color: #444;">
        <div class="flex items-center gap-3">
          <v-icon color="orange" small><slot name="icon">mdi-file-document-outline</slot></v-icon>
          <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">
            <slot name="title">RELATÓRIO GERAL</slot>
          </h2>
        </div>
        <div class="flex-none ml-auto">
          <button
            class="bg-[#F57C00] hover:bg-orange-600 text-white font-black py-2 px-6 rounded-lg shadow-md shadow-orange-900/20 transition-all transform hover:-translate-y-0.5 flex items-center justify-center gap-2 text-[10px] uppercase tracking-widest"
            @click="printReport"
            style="width: 150px; flex-basis: 150px; flex-shrink: 0;"
          >
            <v-icon color="white" small class="mr-1" style="font-size: 14px;">mdi-printer</v-icon>
            IMPRIMIR
          </button>
        </div>
      </div>

      <!-- CONTAINER DO RELATÓRIO PÁGINA BRANCA - report-content-print é referência para impressão -->
      <div class="p-8 md:p-12 report-content-print">
        <!-- CABEÇALHO PADRÃO DO SISTEMA (DENTRO DA PÁGINA) -->
        <header v-if="showHeader" class="flex flex-col items-center justify-center mb-10 pb-8 border-b border-slate-100">
          <div class="w-full text-center mb-6">
             <span class="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em]">{{ $store.getters.getAppName }}</span>
          </div>
          
          <img
            v-if="prefecture"
            :src="prefecture.prefecture_logo ? $store.state.urlImages + prefecture.prefecture_logo : require('@/assets/no-image.jpg')"
            :alt="prefecture.prefecture_social_name || ''"
            class="h-24 object-contain mb-6"
          />
          
          <h1 v-if="prefecture" class="text-2xl font-black text-slate-800 uppercase tracking-tight mb-2 text-center">
            {{ prefecture.prefecture_social_name || '' }}
          </h1>
          
          <div class="flex flex-col items-center gap-1">
            <h4 v-if="period" class="text-xs font-semibold text-slate-400 uppercase tracking-wider">
              Período: {{ period }}
            </h4>
            <div class="mt-2 text-center">
               <slot name="sub-header"></slot>
            </div>
          </div>
        </header>

        <!-- CONTEÚDO DINÂMICO -->
        <main>
          <slot></slot>
        </main>

        <!-- RODAPÉ DINÂMICO -->
        <footer class="mt-12 break-inside-avoid">
          <slot name="footer"></slot>
        </footer>
      </div> <!-- FIM DO PADDING BRANCO -->
    </div> <!-- FIM DO CONTAINER ARREDONDADO -->
  </div> <!-- FIM DO LAYOUT MODERNO -->
</template>

<script>
/**
 * ReportLayoutModerno
 * Modelo de layout padrão para relatórios SCI Moderno.
 * Permite reutilizar o cabeçalho e estrutura de containers em qualquer relatório.
 */
export default {
  name: "ReportLayoutModerno",
  props: {
    prefecture: {
      type: Object,
      required: true,
      default: () => ({})
    },
    period: {
      type: String,
      default: ""
    },
    showHeader: {
      type: Boolean,
      default: true
    }
  },
  methods: {
    printReport() {
      // Captura o HTML do relatório
      const reportEl = this.$el.querySelector('.report-content-print');
      const content = reportEl ? reportEl.innerHTML : this.$el.innerHTML;

      // Cria um iframe oculto para impressão (mais confiável que popup)
      const iframe = document.createElement('iframe');
      iframe.style.cssText = 'position:fixed;top:0;left:0;width:0;height:0;border:0;visibility:hidden;';
      document.body.appendChild(iframe);

      const doc = iframe.contentDocument || iframe.contentWindow.document;

      // Escreve o HTML básico (sem CSS inline para evitar quebra por </style>)
      doc.open();
      doc.write('<!DOCTYPE html><html lang="pt-BR" class="white-bg-report"><head><meta charset="UTF-8"><title>Relatório TCE</title></head><body class="white-bg-report">' + content + '</body></html>');
      doc.close();

      // Injeta links de fontes e ícones
      // Fonte de sistema Arial — sem necessidade de Google Fonts

      const mdiLink = doc.createElement('link');
      mdiLink.rel = 'stylesheet';
      mdiLink.href = 'https://cdn.jsdelivr.net/npm/@mdi/font@latest/css/materialdesignicons.min.css';
      doc.head.appendChild(mdiLink);

      // Coleta e injeta todos os CSS do documento pai via textContent (seguro para </style> no CSS)
      Array.from(document.styleSheets).forEach(sheet => {
        try {
          let css = '';
          Array.from(sheet.cssRules || []).forEach(rule => {
            // Exclui regras @media print que esconderiam conteúdo na janela de impressão
            if (!(rule instanceof CSSMediaRule && rule.conditionText && rule.conditionText.includes('print'))) {
              css += rule.cssText + '\n';
            }
          });
          if (css) {
            const style = doc.createElement('style');
            style.textContent = css;
            doc.head.appendChild(style);
          }
        } catch (e) {
          // Ignora folhas cross-origin
        }
      });

      // Estilos base para impressão limpa
      const baseStyle = doc.createElement('style');
      baseStyle.textContent = `
        @page { size: A4 landscape; margin: 10mm 12mm 10mm 12mm; }
        * { box-sizing: border-box; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
        html, body { font-family: Arial, Helvetica, sans-serif; background: white !important; background-color: white !important; color: #111; padding: 8px; margin: 0; font-size: 10px; }
        .page-break { page-break-before: always !important; break-before: page !important; }
        table { border-collapse: collapse; width: 100%; }
        th, td { vertical-align: middle; }
      `;
      doc.head.appendChild(baseStyle);

      // Aguarda estilos e fontes carregarem, depois imprime
      setTimeout(() => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
        // Remove o iframe após a impressão
        setTimeout(() => {
          document.body.removeChild(iframe);
        }, 2000);
      }, 800);
    }

  }
}
</script>

<style>
@media print {
  /* ISOLAMENTO AGRESSIVO - FORÇA O RELATÓRIO A SER O ÚNICO ELEMENTO NO PAPEL */
  
  /* 1. Esconde tudo o que não é o layout do relatório ou seus pais */
  body > *:not(.v-application),
  .v-navigation-drawer,
  .v-app-bar,
  .v-footer,
  .v-snack,
  .v-dialog__content,
  .print\:hidden {
    display: none !important;
  }

  /* 2. Reseta os containers com posicionamento absoluto para garantir que o relatório "salte" para o topo */
  html, body {
    height: auto !important;
    overflow: visible !important;
    background: white !important;
    background-color: white !important;
  }

  #app, .v-application, .v-application--wrap, .v-main, .v-main__wrap, .sci-page-wrapper, .max-w-7xl {
    position: static !important;
    display: block !important;
    height: auto !important;
    min-height: auto !important;
    width: 100% !important;
    margin: 0 !important;
    padding: 0 !important;
    overflow: visible !important;
    box-shadow: none !important;
    background: white !important;
    background-color: white !important;
  }

  /* 3. Posiciona o layout do relatório de forma independente */
  .report-layout-moderno {
    position: absolute !important;
    top: 0 !important;
    left: 0 !important;
    width: 100% !important;
    z-index: 9999 !important;
    background: white !important;
    margin: 0 !important;
    padding: 0 !important;
    display: block !important;
  }

  /* 4. Garante que o texto seja legível e as bordas apareçam */
  * {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
    overflow: visible !important;
  }

  /* Remove sombras e paddings excessivos que podem empurrar o conteúdo */
  .p-8, .md\:p-12, .report-content-print {
    padding: 8px !important;
  }

  .shadow-sm, .rounded-xl, .border {
    box-shadow: none !important;
    border-radius: 0 !important;
  }
  .page-break {
    page-break-before: always !important;
    break-before: page !important;
  }
}
</style>

<style scoped>
.report-layout-moderno {
  font-family: Arial, Helvetica, sans-serif;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}
</style>
